// frontend/src/pages/SignatureCapturePage.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { obtenerTipoDispositivo } from '../utils/detector-dispositivo';
import { API_BASE_URL } from '../utils/apiConfig';
import { obtenerUbicacionAmigable } from '../utils/geoLocationService';

function obtenerNombreNavegador() {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  return 'Otro';
}

function SignatureCapturePage() {
  const canvasRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const pollingIntervalRef = useRef(null); // ⭐ V5.4: Ref para polling de estado
  const iframeLoadCountRef = useRef(0);   // ⭐ V5.4: Contar navigaciones del iframe
  
  const [mode, setMode] = useState('hand');
  const [typedName, setTypedName] = useState('');
  const [ipPublica, setIpPublica] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [canvasKey, setCanvasKey] = useState(0);
  const [correo, setCorreo] = useState('');
  
  const [mostrarCerticamara, setMostrarCerticamara] = useState(false);
  const [linkCerticamara, setLinkCerticamara] = useState('');
  const [esperandoLinkCerticamara, setEsperandoLinkCerticamara] = useState(false);
  const [certicamaraError, setCerticamaraError] = useState('');
  // ⭐ V5.6: Estado para mostrar popup de rechazo antes de redirigir
  const [showRejected, setShowRejected] = useState(false);
  
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const photo = location.state?.selfie;
  const firmanteIndex = location.state?.firmanteIndex;

  const saltarBiofirmas = location.state?.saltarBiofirmas || false;
const linkCerticamaraExistente = location.state?.linkCerticamaraExistente || null;
const skipGrafo = location.state?.skipGrafo || false;
const skipFoto  = location.state?.skipFoto  || false;





  // ═══════════════════════════════════════════════════════════════════
  // OBTENER DATOS INICIALES
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => {
    const obtenerCorreo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/documento-por-cedula/${location.state?.cedula}`);
        const data = await res.json();
        if (data?.documento?.correo) {
          setCorreo(data.documento.correo);
        }
      } catch (err) {
        console.error('Error al obtener el correo del paciente:', err);
      }
    };
    if (location.state?.cedula) {
      obtenerCorreo();
    }
  }, [location.state?.cedula]);

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setIpPublica(data.ip))
      .catch(err => console.warn('No se pudo obtener IP pública:', err));
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // FUNCIONES DEL CANVAS
  // ═══════════════════════════════════════════════════════════════════

  const getCanvasOffset = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = canvasWrapperRef.current;
    if (!canvas || !wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    
    const displayWidth = Math.min(wrapperRect.width - 10, 350);
    const displayHeight = Math.max(wrapperRect.height - 10, 100);

    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    canvas.width = Math.round(displayWidth * ratio);
    canvas.height = Math.round(displayHeight * ratio);

    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    // ⭐ V5.9.2: Sin fondo blanco → firma transparente al exportar
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    canvas.setAttribute('willReadFrequently', 'true');
    hasInkRef.current = false;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;

    // ⭐ V5.9.2: Limpiar dejando transparente
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    hasInkRef.current = false;
  }, []);

  const canvasHasDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const ctx = canvas.getContext('2d');

    try {
      const sampleW = Math.min(canvas.width, 100);
      const sampleH = Math.min(canvas.height, 50);
      const imgData = ctx.getImageData(0, 0, sampleW, sampleH).data;

      let nonWhitePixels = 0;
      for (let i = 0; i < imgData.length; i += 4) {
        const r = imgData[i];
        const g = imgData[i + 1];
        const b = imgData[i + 2];
        const alpha = imgData[i + 3];

        if (alpha > 0 && !(r === 255 && g === 255 && b === 255)) {
          nonWhitePixels++;
        }
      }

      return nonWhitePixels >= 10;
    } catch (err) {
      return true;
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // ⭐ SPRINT 4: RECORTAR ESPACIO VACÍO ALREDEDOR DE LA FIRMA
  // ───────────────────────────────────────────────────────────────────
  // Detecta los límites reales del trazo (dónde hay tinta) y devuelve un
  // PNG recortado solo al trazo, sin el espacio transparente alrededor.
  // Así, sin importar si la persona firmó arriba, en el centro o abajo del
  // lienzo, la firma queda "pegada" a sus bordes y al estamparla en la caja
  // del documento se ve bien proporcionada y no flotando.
  // Si algo falla, devuelve null y se usa la firma sin recortar (fallback).
  // ═══════════════════════════════════════════════════════════════════
  const recortarFirma = useCallback((canvasOrigen) => {
    try {
      const ctx = canvasOrigen.getContext('2d', { willReadFrequently: true });
      const { width, height } = canvasOrigen;
      const imgData = ctx.getImageData(0, 0, width, height).data;

      let minX = width, minY = height, maxX = 0, maxY = 0;
      let hayTinta = false;

      // Recorrer todos los píxeles buscando los que tienen tinta (alpha > 0)
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const alpha = imgData[(y * width + x) * 4 + 3];
          if (alpha > 10) { // umbral pequeño para ignorar antialias casi invisible
            hayTinta = true;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (!hayTinta) return null; // lienzo vacío → sin recorte

      // Margen pequeño alrededor del trazo para que no quede pegado al borde
      const margen = 8;
      minX = Math.max(0, minX - margen);
      minY = Math.max(0, minY - margen);
      maxX = Math.min(width, maxX + margen);
      maxY = Math.min(height, maxY + margen);

      const recorteW = maxX - minX;
      const recorteH = maxY - minY;
      if (recorteW <= 0 || recorteH <= 0) return null;

      // Crear un canvas nuevo solo con el área del trazo
      const canvasRecortado = document.createElement('canvas');
      canvasRecortado.width = recorteW;
      canvasRecortado.height = recorteH;
      const ctxRec = canvasRecortado.getContext('2d');
      ctxRec.drawImage(
        canvasOrigen,
        minX, minY, recorteW, recorteH,
        0, 0, recorteW, recorteH
      );

      return canvasRecortado.toDataURL('image/png');
    } catch (err) {
      console.warn('⚠️ [RECORTE] No se pudo recortar la firma, se usará sin recortar:', err.message);
      return null; // fallback seguro
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // INICIALIZAR CANVAS Y EVENT LISTENERS
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (mode !== 'hand' || mostrarCerticamara) return;

    const timer = setTimeout(() => {
      initCanvas();

      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.style.touchAction = 'none';
      const originalBodyOverscroll = document.body.style.overscrollBehavior;
      document.body.style.overscrollBehavior = 'none';

      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      const onPointerDown = (e) => {
        if (e.button && e.button !== 0) return;
        e.preventDefault();
        canvas.setPointerCapture(e.pointerId);
        isDrawingRef.current = true;
        const { x, y } = getCanvasOffset(e.clientX, e.clientY);
        lastPointRef.current = { x, y };
        ctx.beginPath();
        ctx.moveTo(x, y);
      };

      const onPointerMove = (e) => {
        if (!isDrawingRef.current) return;
        e.preventDefault();
        const { x, y } = getCanvasOffset(e.clientX, e.clientY);
        ctx.lineTo(x, y);
        ctx.stroke();
        lastPointRef.current = { x, y };
        hasInkRef.current = true;
      };

      const endPointer = (e) => {
        if (isDrawingRef.current) {
          try {
            canvas.releasePointerCapture && canvas.releasePointerCapture(e.pointerId);
          } catch (err) {}
          isDrawingRef.current = false;
        }
      };

      const onTouchMovePrevent = (ev) => {
        if (ev.touches && ev.touches.length === 1 && isDrawingRef.current) {
          ev.preventDefault();
        }
      };

      canvas.addEventListener('pointerdown', onPointerDown);
      canvas.addEventListener('pointermove', onPointerMove);
      canvas.addEventListener('pointerup', endPointer);
      canvas.addEventListener('pointercancel', endPointer);
      canvas.addEventListener('touchmove', onTouchMovePrevent, { passive: false });

      return () => {
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', endPointer);
        canvas.removeEventListener('pointercancel', endPointer);
        canvas.removeEventListener('touchmove', onTouchMovePrevent);
        document.body.style.overscrollBehavior = originalBodyOverscroll;
      };
    }, 100);

    return () => clearTimeout(timer);
  }, [mode, canvasKey, initCanvas, getCanvasOffset, mostrarCerticamara]);

  useEffect(() => {
    if (mode !== 'hand' || mostrarCerticamara) return;
    
    const handleResize = () => {
      initCanvas();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mode, initCanvas, mostrarCerticamara]);

  // ═══════════════════════════════════════════════════════════════════
  // CAMBIAR MODO
  // ═══════════════════════════════════════════════════════════════════

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    if (newMode === 'hand') {
      setCanvasKey(prev => prev + 1);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // POLLING PARA OBTENER LINK DE CERTICÁMARA
  // ═══════════════════════════════════════════════════════════════════
  
  const consultarLinkCerticamara = useCallback(async (documentoId, intentos = 0) => {
    const maxIntentos = 20;
    
    if (intentos >= maxIntentos) {
      setCerticamaraError('No se pudo obtener el link de firma digital. Por favor intenta más tarde.');
      setEsperandoLinkCerticamara(false);
      return;
    }

    try {
      console.log(`🔍 [CERTICAMARA] Consultando link... intento ${intentos + 1}/${maxIntentos}`);
      
      const url = firmanteIndex !== null && firmanteIndex !== undefined
        ? `${API_BASE_URL}/api/certicamara/link/${documentoId}?firmanteIndex=${firmanteIndex}`
        : `${API_BASE_URL}/api/certicamara/link/${documentoId}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (response.ok && (data.linkFirma || data.link)) {
        const link = data.linkFirma || data.link;
        console.log('✅ [CERTICAMARA] Link obtenido:', link);
        setLinkCerticamara(link);
        setEsperandoLinkCerticamara(false);
        setMostrarCerticamara(true);
        setIsProcessing(false);
        return;
      }
      
      console.log('⏳ [CERTICAMARA] Link no disponible aún, reintentando en 3s...');
      setTimeout(() => {
        consultarLinkCerticamara(documentoId, intentos + 1);
      }, 3000);
      
    } catch (error) {
      console.error('❌ [CERTICAMARA] Error consultando link:', error);
      setTimeout(() => {
        consultarLinkCerticamara(documentoId, intentos + 1);
      }, 3000);
    }
  }, [firmanteIndex]);

  // ⭐ V5.8: Si viene con saltarBiofirmas, ir directo a Certicámara
useEffect(() => {
  if (!saltarBiofirmas) return;

  console.log('⚡ [V5.8] Saltando BIOFIRMAS → directo a Certicámara');

  if (linkCerticamaraExistente) {
    // Ya tenemos el link, mostrar iframe directamente
    setLinkCerticamara(linkCerticamaraExistente);
    setMostrarCerticamara(true);
  } else {
    // Hacer polling hasta obtener el link
    setIsProcessing(true);
    setProcessingMessage('Recuperando tu firma digital...');
    setEsperandoLinkCerticamara(true);
    consultarLinkCerticamara(id);
  }
}, [saltarBiofirmas]);

// ⭐ V5.8: Si no requiere grafo → saltar canvas y enviar firma vacía
useEffect(() => {
  if (!skipGrafo || saltarBiofirmas) return;

  console.log('⚡ [V5.8] skipGrafo=true → generando firma vacía y continuando');

  // Selfie real o imagen en blanco si skipFoto=true
// ⭐ Si no hay selfie (skipFoto=true), generar imagen blanca
const selfieData = location.state?.selfie || (() => {
  const c = document.createElement('canvas');
  c.width = 100; c.height = 100;
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 100, 100);
  return c.toDataURL();
})();

  // Generar firma vacía (canvas blanco)
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 100;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 400, 100);
  const firmaVacia = canvas.toDataURL();

  setIsProcessing(true);
  setProcessingMessage('Preparando documento...');

  fetch(`${API_BASE_URL}/api/guardar-firma/${id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      signature: firmaVacia,
      selfie: selfieData,
      firmanteIndex
    })
  })
  .then(async (res) => {
    if (!res.ok) throw new Error('Error guardando firma vacía');
    // Continuar con generar PDF final
    const { obtenerUbicacionAmigable } = await import('../utils/geoLocationService');
    const ubicacion = await obtenerUbicacionAmigable().catch(() => ({ lat: '', lon: '', mensaje: '' }));

    return fetch(`${API_BASE_URL}/api/generar-pdf-final/${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat: ubicacion.lat,
        lon: ubicacion.lon,
        ip: '',
        dispositivo: 'N/A',
        navegador: 'N/A',
        ubicacionInfo: ubicacion.mensaje,
        firmanteIndex
      })
    });
  })
  .then(async (res) => {
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      if (data.enviadoACerticamara) {
        if (data.linkFirma) {
          setLinkCerticamara(data.linkFirma);
          setMostrarCerticamara(true);
          setIsProcessing(false);
        } else {
          setEsperandoLinkCerticamara(true);
          setProcessingMessage('Esperando link de firma digital...');
          consultarLinkCerticamara(id);
        }
      } else {
        setIsProcessing(false);
        setShowSuccess(true);
        setTimeout(() => { window.location.href = 'https://colombianadetrasplantes.com'; }, 3000);
      }
    } else {
      throw new Error(data.message || 'Error generando documento');
    }
  })
  .catch((err) => {
    console.error('❌ [V5.8] Error en flujo skipGrafo:', err);
    setIsProcessing(false);
    alert('Error al procesar el documento. Intenta nuevamente.');
  });
}, [skipGrafo, id]);

  // ═══════════════════════════════════════════════════════════════════
  // ⭐ V5.6: POLLING AUTOMÁTICO DE ESTADO — DOBLE VALIDACIÓN
  // El endpoint /api/certicamara/link/ ahora consulta Certicámara
  // directamente (con caché de 10s) además de MongoDB.
  // Detecta FIRMADO y RECHAZADO → redirige a colombianadetrasplantes.com
  // Solo queda en el iframe si estado = pendiente/en progreso
  // ═══════════════════════════════════════════════════════════════════

  const verificarEstadoFirma = useCallback(async (documentoId) => {
    try {
      const url = firmanteIndex !== null && firmanteIndex !== undefined
        ? `${API_BASE_URL}/api/certicamara/link/${documentoId}?firmanteIndex=${firmanteIndex}`
        : `${API_BASE_URL}/api/certicamara/link/${documentoId}`;

      const response = await fetch(url);
      const data = await response.json();

      const estado = data.estado || data.estadoCerticamara || '';
      const estaFirmado = estado === 'firmado' || estado === 'SIGNED' || estado === 'COMPLETE';
      
      // ⭐ V5.6: Detectar rechazo o cancelación (incluye flag directo del endpoint)
      const estaRechazado = estado === 'rechazado' || estado === 'REJECTED' || estado === 'cancelado' || data.rechazado === true;

      // ────────────────────────────────────────
      // FIRMADO → Popup éxito verde → Redirigir a colombiana (3s)
      // ────────────────────────────────────────
      if (estaFirmado) {
        console.log('✅ [CERTICAMARA] ¡Firma detectada! Estado:', estado);
        
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setMostrarCerticamara(false);
        setShowSuccess(true);
        setTimeout(() => {
          window.location.href = 'https://colombianadetrasplantes.com';
        }, 3000);
        return true;
      }

      // ────────────────────────────────────────
      // ⭐ V5.6: RECHAZADO → Popup rojo → Redirigir a colombiana (4s)
      // ────────────────────────────────────────
      if (estaRechazado) {
        console.log('🚫 [CERTICAMARA] Documento rechazado. Estado:', estado);
        
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setMostrarCerticamara(false);
        setShowRejected(true);
        setTimeout(() => {
          window.location.href = 'https://colombianadetrasplantes.com';
        }, 4000);
        return true;
      }

      // Si no es firmado ni rechazado → sigue pendiente, no hacer nada
      return false;
    } catch (error) {
      console.warn('⚠️ [CERTICAMARA] Error verificando estado:', error.message);
      return false;
    }
  }, [firmanteIndex]);

  const iniciarPollingEstadoFirma = useCallback((documentoId) => {
    console.log('🔄 [CERTICAMARA] Iniciando polling de estado (cada 3s)...');

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      verificarEstadoFirma(documentoId);
    }, 3000); // ⭐ Cada 3 segundos
  }, [verificarEstadoFirma]);

  // ⭐ V5.4: Cuando el iframe navega (usuario avanza pasos o hace clic en "Cerrar ventana")
  // verificamos inmediatamente sin esperar al polling
  const handleIframeLoad = useCallback(() => {
    iframeLoadCountRef.current += 1;
    console.log(`📄 [CERTICAMARA] Iframe load #${iframeLoadCountRef.current}`);

    // Ignorar el primer load (carga inicial del iframe)
    if (iframeLoadCountRef.current > 1 && id) {
      console.log('🔍 [CERTICAMARA] Navegación detectada en iframe, verificando estado inmediatamente...');
      // Pequeño delay para dar tiempo al webhook de llegar al backend
      setTimeout(() => verificarEstadoFirma(id), 1500);
    }
  }, [id, verificarEstadoFirma]);

  // ⭐ V5.4: Iniciar polling cuando se muestra el iframe
  useEffect(() => {
    if (mostrarCerticamara && linkCerticamara && id) {
      iframeLoadCountRef.current = 0; // Reset al mostrar iframe
      iniciarPollingEstadoFirma(id);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [mostrarCerticamara, linkCerticamara, id, iniciarPollingEstadoFirma]);

  // Limpiar polling al desmontar componente
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // ENVIAR FIRMA
  // ═══════════════════════════════════════════════════════════════════

  const handleSubmit = async () => {
    if (!photo && !skipFoto) {
  alert('No se encontró la selfie. Debes tomarla antes de firmar.');
  return;
}

    if (mode === 'typed' && !typedName.trim()) {
      alert('Por favor, escribe tu nombre completo para continuar.');
      return;
    }

    if (mode === 'hand') {
      if (!hasInkRef.current && !canvasHasDrawing()) {
        alert('Por favor, dibuja tu firma antes de continuar.');
        return;
      }
    }

    setIsProcessing(true);
    setProcessingMessage('Procesando tu firma...');

    try {
      let signatureData;
      if (mode === 'hand') {
        // ⭐ SPRINT 4: recortar el espacio vacío alrededor del trazo.
        // Si el recorte falla, recortada será null y usamos el PNG completo.
        const recortada = recortarFirma(canvasRef.current);
        signatureData = recortada || canvasRef.current.toDataURL();
      } else {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400;
        canvas.height = 100;
        // ⭐ V5.9.2: Sin fondo blanco → firma transparente
        // ⭐ V5.9.3: Ajustar tamaño de fuente para que el nombre completo quepa
        const maxWidth  = canvas.width - 20;   // margen lateral
        const maxHeight = canvas.height - 20;  // margen vertical
        let fontSize = 36;
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        do {
          ctx.font = `${fontSize}px "Dancing Script", cursive`;
          const metrics = ctx.measureText(typedName);
          const textHeight = (metrics.actualBoundingBoxAscent || fontSize * 0.8) +
                             (metrics.actualBoundingBoxDescent || fontSize * 0.2);
          if (metrics.width <= maxWidth && textHeight <= maxHeight) break;
          fontSize -= 1;
        } while (fontSize > 10);
        ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
        signatureData = canvas.toDataURL();
      }

      setProcessingMessage('Guardando firma y selfie...');

      // ⭐ V5.8: Si skipFoto, generar imagen blanca como placeholder
const selfieParaEnviar = photo || (() => {
  const c = document.createElement('canvas');
  c.width = 100; c.height = 100;
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 100, 100);
  return c.toDataURL();
})();

const saveRes = await fetch(`${API_BASE_URL}/api/guardar-firma/${id}`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ 
    signature: signatureData, 
    selfie: selfieParaEnviar,
    firmanteIndex: firmanteIndex
  })
});

      if (!saveRes.ok) {
        const errorData = await saveRes.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al guardar firma');
      }

      setProcessingMessage('Verificando ubicación...');
      const ubicacion = await obtenerUbicacionAmigable();

      setProcessingMessage('Enviando a firma digital...');
      const tipoDispositivo = obtenerTipoDispositivo();
      const navegador = obtenerNombreNavegador();

      const finalRes = await fetch(`${API_BASE_URL}/api/generar-pdf-final/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: ubicacion.lat,
          lon: ubicacion.lon,
          ip: ipPublica,
          dispositivo: tipoDispositivo,
          navegador,
          ubicacionInfo: ubicacion.mensaje,
          firmanteIndex: firmanteIndex 
        })
      });

      const finalData = await finalRes.json().catch(() => ({}));

      if (finalRes.ok) {
        if (finalData.enviadoACerticamara) {
          console.log('📋 [FIRMA] Documento ADMINISTRATIVO - Redirigiendo a Certicámara');
          
          if (finalData.linkFirma) {
            setLinkCerticamara(finalData.linkFirma);
            setMostrarCerticamara(true);
            setIsProcessing(false);
          } else {
            setEsperandoLinkCerticamara(true);
            setProcessingMessage('Esperando link de firma digital...');
            consultarLinkCerticamara(id);
          }
        } else {
          console.log('📋 [FIRMA] Documento MÉDICO - Finalizado localmente');
          setIsProcessing(false);
          setShowSuccess(true);
          setTimeout(() => {
            window.location.href = 'https://colombianadetrasplantes.com';
          }, 3000);
        }
      } else {
        throw new Error(finalData.message || 'Error al generar documento final');
      }
    } catch (err) {
      console.error('Error general al firmar:', err);
      setIsProcessing(false);

      if (err.message.includes('410') || err.message.includes('Gone')) {
        alert('El documento ha expirado. Solicita un nuevo enlace.');
      } else if (err.message.includes('404') || err.message.includes('Not Found')) {
        alert('Documento no encontrado.');
      } else {
        alert('Error en la conexión. Intenta nuevamente.');
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // COMPONENTES UI
  // ═══════════════════════════════════════════════════════════════════

  const LoadingPopup = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white', padding: '24px', borderRadius: '15px',
        textAlign: 'center', maxWidth: '280px', width: '90%'
      }}>
        <div style={{
          width: '40px', height: '40px', border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff', borderRadius: '50%',
          animation: 'spin 1s linear infinite', margin: '0 auto 16px'
        }}></div>
        <p style={{ fontWeight: 'bold', color: '#333', margin: 0, fontSize: '14px' }}>{processingMessage}</p>
        
        {esperandoLinkCerticamara && (
          <p style={{ color: '#666', fontSize: '12px', marginTop: '10px' }}>
            Esto puede tomar unos segundos...
          </p>
        )}
      </div>
    </div>
  );

  const SuccessPopup = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.85)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 9999,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        backgroundColor: 'white', padding: '30px', borderRadius: '20px',
        textAlign: 'center', maxWidth: '340px', width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        animation: 'scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }}>
        <div style={{
          width: '70px', height: '70px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)',
          animation: 'bounceIn 0.6s ease'
        }}>
          <svg width="35" height="35" viewBox="0 0 24 24" fill="none"
            stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" style={{
              strokeDasharray: 30, strokeDashoffset: 0,
              animation: 'drawCheck 0.6s ease 0.2s both'
            }} />
          </svg>
        </div>

        <h2 style={{ color: '#1f2937', fontSize: '22px', fontWeight: '700', margin: '0 0 8px 0' }}>
          ¡Documento Firmado!
        </h2>

        <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 6px 0' }}>
          Tu documento ha sido firmado exitosamente con certificado digital.
        </p>

        <p style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 20px 0' }}>
          📧 Recibirás copia en tu correo.
        </p>

        <div style={{
          width: '100%', height: '5px', backgroundColor: '#e5e7eb',
          borderRadius: '3px', overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            animation: 'progressBar 3s linear forwards',
            borderRadius: '3px'
          }}></div>
        </div>

        <p style={{ color: '#9ca3af', fontSize: '12px', margin: '12px 0 0 0' }}>
          Redirigiendo...
        </p>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  // ⭐ V5.6: POPUP DE DOCUMENTO RECHAZADO
  // Se muestra cuando el polling detecta rechazo, redirige en 4 segundos
  // ═══════════════════════════════════════════════════════════════════

  const RejectedPopup = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.85)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 9999,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        backgroundColor: 'white', padding: '30px', borderRadius: '20px',
        textAlign: 'center', maxWidth: '340px', width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        animation: 'scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }}>
        <div style={{
          width: '70px', height: '70px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 10px 30px rgba(239, 68, 68, 0.4)',
          animation: 'bounceIn 0.6s ease'
        }}>
          <svg width="35" height="35" viewBox="0 0 24 24" fill="none"
            stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>

        <h2 style={{ color: '#1f2937', fontSize: '22px', fontWeight: '700', margin: '0 0 8px 0' }}>
          Documento Rechazado
        </h2>

        <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 6px 0' }}>
          El proceso de firma ha sido rechazado o cancelado.
        </p>

        <p style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 20px 0' }}>
          Si fue un error, contacta al remitente del documento.
        </p>

        <div style={{
          width: '100%', height: '5px', backgroundColor: '#e5e7eb',
          borderRadius: '3px', overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #ef4444, #f87171)',
            animation: 'progressBar 4s linear forwards',
            borderRadius: '3px'
          }}></div>
        </div>

        <p style={{ color: '#9ca3af', fontSize: '12px', margin: '12px 0 0 0' }}>
          Redirigiendo...
        </p>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  // ⭐ V5.4: CERTICÁMARA EMBEBIDO — SIN BOTÓN "YA FIRMÉ"
  // El iframe ocupa toda la pantalla. El polling automático detecta
  // cuando la firma se completa y redirige sin intervención del usuario.
  // ═══════════════════════════════════════════════════════════════════
  
  const CerticamaraEmbed = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#f8f9fa',
      zIndex: 9998,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1e4b',
        color: 'white',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/logo.png" alt="Logo" style={{ height: '36px' }} />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Firma Digital</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Certicámara · Firma Electrónica Certificada</div>
          </div>
        </div>
        <div style={{
          backgroundColor: '#10b981',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          🔒 Conexión Segura
        </div>
      </div>

      {/* Instrucciones */}
      <div style={{
        backgroundColor: '#e0f2fe',
        borderBottom: '1px solid #bae6fd',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontSize: '20px' }}>👇</span>
        <div>
          <strong style={{ color: '#0369a1' }}>Último paso:</strong>
          <span style={{ color: '#0c4a6e', marginLeft: '8px' }}>
            Completa tu firma digital en el formulario de abajo y presiona "Siguiente"
          </span>
        </div>
      </div>

      {/* Iframe de Certicámara — ocupa TODO el espacio restante */}
      <div style={{ flex: 1, position: 'relative' }}>
        <iframe
          src={linkCerticamara}
          title="Firma Digital Certicámara"
          onLoad={handleIframeLoad}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          allow="camera; microphone"
        />
      </div>

      {/* ⭐ V5.4: SIN BOTÓN "YA FIRMÉ" — El polling detecta automáticamente */}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  if (certicamaraError) {
    return (
      <div style={{
        maxWidth: '500px',
        margin: '40px auto',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '30px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ color: '#dc2626', marginBottom: '12px' }}>Error de Conexión</h2>
          <p style={{ color: '#7f1d1d', marginBottom: '20px' }}>{certicamaraError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  if (mostrarCerticamara && linkCerticamara) {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { opacity: 0; transform: scale(0.8) translateY(20px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
            @keyframes bounceIn {
              0% { opacity: 0; transform: scale(0.3); }
              50% { transform: scale(1.1); }
              70% { transform: scale(0.9); }
              100% { opacity: 1; transform: scale(1); }
            }
            @keyframes drawCheck {
              from { stroke-dashoffset: 30; }
              to { stroke-dashoffset: 0; }
            }
            @keyframes progressBar {
              from { width: 0%; }
              to { width: 100%; }
            }
          `}
        </style>
        <CerticamaraEmbed />
        {showSuccess && <SuccessPopup />}
        {showRejected && <RejectedPopup />}
      </>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.8) translateY(20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes bounceIn {
            0% { opacity: 0; transform: scale(0.3); }
            50% { transform: scale(1.1); }
            70% { transform: scale(0.9); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes drawCheck {
            from { stroke-dashoffset: 30; }
            to { stroke-dashoffset: 0; }
          }
          @keyframes progressBar {
            from { width: 0%; }
            to { width: 100%; }
          }

          * {
            box-sizing: border-box;
          }

          .sig-container {
            width: 100%;
            max-width: 500px;
            margin: 0 auto;
            padding: 10px 14px;
            background: #f8f9fa;
            height: 100vh;
            height: 75dvh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .sig-header {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 10px;
            padding: 4px 0;
            flex-shrink: 0;
          }

          .sig-header img {
            height: 32px;
            object-fit: contain;
          }

          .sig-title {
            text-align: center;
            color: #1f2937;
            font-size: 18px;
            font-weight: 700;
            margin: 6px 0 2px 0;
            flex-shrink: 0;
          }

          .sig-subtitle {
            text-align: center;
            color: #6b7280;
            font-size: 12px;
            margin: 0 0 8px 0;
            flex-shrink: 0;
          }

          .mode-selector {
            display: flex;
            gap: 8px;
            margin-bottom: 8px;
            flex-shrink: 0;
          }

          .mode-btn {
            flex: 1;
            padding: 8px 6px;
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }

          .mode-btn:hover {
            border-color: #93c5fd;
          }

          .mode-btn.active {
            border-color: #2563eb;
            background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          }

          .mode-btn .icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: #f3f4f6;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            flex-shrink: 0;
          }

          .mode-btn.active .icon {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          }

          .mode-btn .label {
            font-weight: 600;
            font-size: 13px;
            color: #374151;
          }

          .mode-btn.active .label {
            color: #1d4ed8;
          }

          .sig-area {
            flex: 1;
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 8px;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow: hidden;
          }

          .sig-hint {
            text-align: center;
            color: #6b7280;
            font-size: 12px;
            margin-bottom: 6px;
            flex-shrink: 0;
          }

          .canvas-wrapper {
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 0;
            background: #fafafa;
            border-radius: 8px;
            border: 1px dashed #d1d5db;
          }

          .canvas-wrapper canvas {
            border-radius: 8px;
            background: white;
            cursor: crosshair;
            touch-action: none;
          }

          .btn-clear {
            align-self: center;
            background: #f3f4f6;
            color: #4b5563;
            border: none;
            padding: 8px 26px;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            margin-top: 6px;
            flex-shrink: 0;
          }

          .typed-input {
            width: 100%;
            padding: 10px;
            font-size: 16px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            text-align: center;
            margin-bottom: 8px;
            flex-shrink: 0;
          }

          .typed-input:focus {
            outline: none;
            border-color: #2563eb;
          }

          .typed-preview {
            flex: 1;
            font-family: 'Dancing Script', cursive;
            font-size: 28px;
            color: #1f2937;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fafafa;
            border-radius: 8px;
            border: 1px dashed #d1d5db;
            min-height: 0;
          }

          .btn-submit {
            width: 100%;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: white;
            border: none;
            padding: 12px;
            border-radius: 100px;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
            margin-top: 8px;
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
            flex-shrink: 0;
          }

          .btn-submit:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            box-shadow: none;
          }

          .sig-footer {
            text-align: center;
            color: #9ca3af;
            font-size: 10px;
            margin-top: 6px;
            flex-shrink: 0;
          }
        `}
      </style>

      <div className="sig-container">
        <div className="sig-header">
          <img src="/logo2.png" alt="Logo" />
          <img src="/logo3.png" alt="Logo" />
        </div>

        <h1 className="sig-title">Firma del Documento</h1>
        <p className="sig-subtitle">Selecciona cómo deseas firmar</p>

        <div className="mode-selector">
          <div
            className={`mode-btn ${mode === 'hand' ? 'active' : ''}`}
            onClick={() => !isProcessing && handleModeChange('hand')}
          >
            <div className="icon">✍️</div>
            <div className="label">Mano Alzada</div>
          </div>
          <div
            className={`mode-btn ${mode === 'typed' ? 'active' : ''}`}
            onClick={() => !isProcessing && handleModeChange('typed')}
          >
            <div className="icon">⌨️</div>
            <div className="label">Con Texto</div>
          </div>
        </div>

        <div className="sig-area">
          {mode === 'hand' ? (
            <>
              <p className="sig-hint">👆 Dibuja tu firma aquí</p>
              <div className="canvas-wrapper" ref={canvasWrapperRef}>
                <canvas
                  key={canvasKey}
                  ref={canvasRef}
                />
              </div>
              <button onClick={clearCanvas} className="btn-clear" disabled={isProcessing}>
                🗑️ Limpiar
              </button>
            </>
          ) : (
            <>
              <p className="sig-hint">✏️ Escribe tu nombre completo</p>
              <input
                type="text"
                className="typed-input"
                placeholder="Tu nombre completo"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={isProcessing}
                autoComplete="off"
              />
              <div className="typed-preview">
                {typedName || 'Tu firma aquí'}
              </div>
            </>
          )}
        </div>

        <button onClick={handleSubmit} className="btn-submit" disabled={isProcessing}>
          {isProcessing ? '⏳ Procesando...' : '✅ Continuar a Firma Digital'}
        </button>

        <p className="sig-footer">🔒 Firma protegida con certificación digital Certicámara</p>
      </div>

      {isProcessing && <LoadingPopup />}
      {showSuccess && <SuccessPopup />}
      {showRejected && <RejectedPopup />}
    </>
  );
}

export default SignatureCapturePage;